import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import ProductType from 'models/enums/ProductType';
import ProductPricingAdjustmentButton from 'module/productManagement/components/productPricingButton/ProductPricingAdjustmentButton';
import { isMobile } from 'react-device-detect';
import { ProductEnhancementAction, useProductEnhancementProvider } from '../ProductEnhancement.Hooks';
import ProductFilters from './ProductEnhancement.Filters';

export default function ProductEnhancementList() {
  const { lang, translate } = getLocales();
  const { listApi, cloneApi, pageRouter, permissions, selected } = useProductEnhancementProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.productEnhancements}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.productEnhancements} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <ProductFilters />}
            <Button
              class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
              active
              media={<Icon class="fas-plus" />}
              render={!isMobile && lang.addNew}
              onClick={pageRouter.updateParams}
              disabled={!permissions.CREATE_PRODUCT}
              data={{ action: ProductEnhancementAction.EDIT }}
            />
          </Box>
        </>
      }
      subHeader={!isMobile && <ProductFilters />}
      render={
        <>
          {selected && pageRouter.params.action === ProductEnhancementAction.CLONE && (
            <Confirm
              data={selected}
              onConfirm={(data) => cloneApi.execute(data.id)}
              onClose={pageRouter.updateParams}
              actionName={lang.cloneProductEnhancement}
              itemName={selected.name}
              confirmationType="nextButton"
              subcomponents={ThemeConfirmDefault}
            />
          )}
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th>{lang.name}</th>
                <th>{lang.type}</th>
                <th>{lang.pricingDistributionTemplate}</th>
                <th data-actions>{lang.actions}</th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={listApi.payload?.data}
                render={({ data: productEnhancement }) => (
                  <tr key={productEnhancement.id}>
                    <td>{productEnhancement.name}</td>
                    <td>{translate(productEnhancement.productType) ?? lang['WARRANTY_TIER-BASED']}</td>
                    <td>{productEnhancement.pricingDistributionTemplate?.name}</td>
                    <td>
                      <Box class={ThemeWrapperStack}>
                        <Button
                          onClick={pageRouter.updateParams}
                          data={{
                            id: productEnhancement.id.toString(),
                            action: ProductEnhancementAction.EDIT,
                          }}
                          disabled={!permissions.EDIT_PRODUCT}
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-edit" />}
                          htmlElementProps={{ title: lang.edit }}
                          active
                        />
                        <Button
                          onClick={pageRouter.updateParams}
                          data={{
                            id: productEnhancement.id.toString(),
                            action: ProductEnhancementAction.DELETE,
                          }}
                          disabled={!permissions.DELETE_PRODUCT}
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-trash" />}
                          htmlElementProps={{ title: lang.delete }}
                          active
                        />
                        <ProductPricingAdjustmentButton
                          id={productEnhancement.id}
                          dataType="productEnhancement"
                          productType={productEnhancement.productType ?? ProductType.warrantyTierBased}
                        />
                        <Button
                          onClick={pageRouter.updateParams}
                          data={{
                            id: productEnhancement.id.toString(),
                            action: ProductEnhancementAction.CLONE,
                          }}
                          disabled={!permissions.CREATE_PRODUCT}
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-copy" />}
                          htmlElementProps={{ title: lang.cloneProductEnhancement }}
                          active
                        />
                      </Box>
                    </td>
                  </tr>
                )}
              />
            </tbody>
          </table>
        </>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
